import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { DataTable } from '@/components/ui/data-table';
import { useReturns } from '@/hooks/useReturns';
import { useToast } from '@/hooks/useToast';
import { formatCurrency, cn } from '@/lib/utils';
import {
  Package,
  Plus,
  RefreshCw,
  Search,
  XCircle,
  CheckCircle,
  Truck,
  CreditCard,
  Clock,
  FileText,
  Eye,
  Send,
  RotateCcw,
} from 'lucide-react';
import { format } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';

const statusConfig: Record<string, { variant: 'default' | 'warning' | 'success' | 'error' | 'info'; label: string; icon: React.ElementType }> = {
  requested: { variant: 'warning', label: 'Requested', icon: Clock },
  approved: { variant: 'info', label: 'Approved', icon: CheckCircle },
  rejected: { variant: 'error', label: 'Rejected', icon: XCircle },
  shipped: { variant: 'info', label: 'Shipped', icon: Truck },
  in_transit: { variant: 'warning', label: 'In Transit', icon: Truck },
  received: { variant: 'success', label: 'Received', icon: Package },
  credited: { variant: 'success', label: 'Credited', icon: CreditCard },
  cancelled: { variant: 'error', label: 'Cancelled', icon: XCircle },
};

export default function ReturnsPage() {
  const toast = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<string | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const { getReturns, getReturn, getStats, approveReturn, rejectReturn, shipReturn, cancelReturn } = useReturns();

  const { data: returnsData, isLoading, error, refetch } = getReturns({
    status: statusFilter !== 'all' ? statusFilter : undefined,
    page: 1,
    page_size: 50,
  });
  const { data: stats } = getStats();
  const { data: selectedReturnData } = getReturn(selectedReturn || '');

  const approveMutation = approveReturn();
  const rejectMutation = rejectReturn();
  const shipMutation = shipReturn();
  const cancelMutation = cancelReturn();

  const returns = returnsData?.returns || [];

  const handleApprove = async (returnId: string) => {
    try {
      await approveMutation.mutateAsync(returnId);
      toast.success('Return approved', 'The return request has been approved.');
    } catch {
      toast.error('Error', 'Failed to approve return.');
    }
  };

  const handleReject = async (returnId: string) => {
    try {
      await rejectMutation.mutateAsync({ returnId, reason: 'Return rejected by admin' });
      toast.success('Return rejected', 'The return request has been rejected.');
    } catch {
      toast.error('Error', 'Failed to reject return.');
    }
  };

  const handleShip = async (returnId: string) => {
    try {
      await shipMutation.mutateAsync({
        returnId,
        tracking_number: 'TRACK' + Date.now(),
        shipping_carrier: 'Standard',
      });
      toast.success('Return shipped', 'The return has been marked as shipped.');
    } catch {
      toast.error('Error', 'Failed to mark return as shipped.');
    }
  };

  const handleCancel = async (returnId: string) => {
    try {
      await cancelMutation.mutateAsync({ returnId, reason: 'Cancelled by requester' });
      toast.success('Return cancelled', 'The return has been cancelled.');
    } catch {
      toast.error('Error', 'Failed to cancel return.');
    }
  };

  const filteredReturns = returns.filter((ret) =>
    ret.rma_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ret.vendor_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const openDetail = (returnId: string) => {
    setSelectedReturn(returnId);
    setIsDetailOpen(true);
  };

  if (error) {
    return (
      <div className="space-y-6">
        <div className="p-4 rounded-lg bg-red-50 border-2 border-red-200">
          <div className="flex items-center gap-3">
            <XCircle className="h-5 w-5 text-red-600" />
            <p className="text-red-900 font-semibold">Error loading returns</p>
            <Button variant="ghost" size="sm" onClick={() => refetch()}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-sm text-red-800 mt-1">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-bold text-text-primary">Vendor Returns</h1>
          <p className="text-text-secondary mt-1">Manage vendor return requests and RMA workflow</p>
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => refetch()}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="mr-2 h-4 w-4" />
                New Return
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Create Vendor Return</DialogTitle>
                <DialogDescription>
                  Submit a return request to the vendor for damaged, expired, or unwanted items.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Vendor *</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select vendor" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="v1">MedSupply Co.</SelectItem>
                        <SelectItem value="v2">Pharma Distributors</SelectItem>
                        <SelectItem value="v3">Lab Equipment Inc.</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>PO Reference</Label>
                    <Select>
                      <SelectTrigger>
                        <SelectValue placeholder="Select PO (optional)" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="po1">PO-2024-001</SelectItem>
                        <SelectItem value="po2">PO-2024-002</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Items</Label>
                  <p className="text-sm text-text-muted mb-2">Select items to return from available inventory.</p>
                  <div className="border rounded-lg p-4 text-center text-text-muted">
                    Item selector would go here
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea placeholder="Additional notes for this return..." rows={3} />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button>Create Return Request</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
        {Object.entries(statusConfig).map(([status, config]) => {
          const Icon = config.icon;
          const count = stats?.by_status[status] || 0;
          return (
            <Card
              key={status}
              className={cn(
                'cursor-pointer transition-all',
                statusFilter === status ? 'ring-2 ring-primary' : ''
              )}
              onClick={() => setStatusFilter(statusFilter === status ? 'all' : status)}
            >
              <CardContent className="pt-4">
                <div className="flex items-center gap-3">
                  <div className={cn('p-2 rounded-lg', getStatusColorClass(config.variant))}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-text-muted">{config.label}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-text-muted" />
              <Input
                placeholder="Search by RMA number or vendor..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {Object.entries(statusConfig).map(([status, config]) => (
                  <SelectItem key={status} value={status}>
                    {config.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      {isLoading ? (
        <TableSkeleton rows={10} columns={7} />
      ) : filteredReturns.length > 0 ? (
        <DataTable
          columns={[
            {
              header: 'RMA Number',
              accessor: (row) => (
                <div>
                  <p className="font-medium font-mono">{row.rma_number}</p>
                  <p className="text-xs text-text-muted">{format(new Date(row.created_at), 'MMM d, yyyy')}</p>
                </div>
              ),
            },
            {
              header: 'Vendor',
              accessor: (row) => <span className="font-medium">{row.vendor_name}</span>,
            },
            {
              header: 'PO Reference',
              accessor: (row) => <span className="text-text-muted">{row.po_number || '-'}</span>,
            },
            {
              header: 'Items',
              accessor: (row) => <span>{row.items.length} item(s)</span>,
            },
            {
              header: 'Expected Credit',
              accessor: (row) => (
                <span className="font-medium text-green-600">{formatCurrency(row.expected_credit)}</span>
              ),
            },
            {
              header: 'Status',
              accessor: (row) => {
                const config = statusConfig[row.status] || statusConfig.requested;
                const Icon = config.icon;
                return (
                  <Badge variant={config.variant}>
                    <Icon className="h-3 w-3 mr-1" />
                    {config.label}
                  </Badge>
                );
              },
            },
            {
              header: 'Actions',
              accessor: (row) => (
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => openDetail(row.return_id)}>
                    <Eye className="h-4 w-4" />
                  </Button>
                  {row.status === 'requested' && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => handleApprove(row.return_id)}>
                        <CheckCircle className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleReject(row.return_id)}>
                        <XCircle className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  {row.status === 'approved' && (
                    <Button size="sm" variant="outline" onClick={() => handleShip(row.return_id)}>
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                  {['requested', 'approved'].includes(row.status) && (
                    <Button size="sm" variant="ghost" onClick={() => handleCancel(row.return_id)}>
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
          data={filteredReturns}
          emptyMessage="No returns found"
        />
      ) : (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 text-text-muted mx-auto mb-4" />
            <p className="text-text-secondary">No returns found</p>
            <p className="text-sm text-text-muted mt-1">
              {searchQuery || statusFilter !== 'all'
                ? 'Try adjusting your filters'
                : 'Create your first return request to get started'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Return Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Return Details</DialogTitle>
            <DialogDescription>RMA: {selectedReturnData?.rma_number}</DialogDescription>
          </DialogHeader>
          {selectedReturnData && (
            <div className="space-y-6 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-text-muted">Vendor</p>
                  <p className="font-medium">{selectedReturnData.vendor_name}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">PO Reference</p>
                  <p className="font-medium">{selectedReturnData.po_number || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-text-muted">Status</p>
                  <Badge variant={statusConfig[selectedReturnData.status]?.variant || 'default'}>
                    {statusConfig[selectedReturnData.status]?.label || selectedReturnData.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-text-muted">Expected Credit</p>
                  <p className="font-medium text-green-600">
                    {formatCurrency(selectedReturnData.expected_credit)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-medium mb-3">Return Items</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-background-subtle">
                      <tr>
                        <th className="px-4 py-2 text-left">Item</th>
                        <th className="px-4 py-2 text-left">Qty</th>
                        <th className="px-4 py-2 text-left">Unit Price</th>
                        <th className="px-4 py-2 text-left">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedReturnData.items.map((item, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-4 py-2">
                            <p className="font-medium">{item.item_name}</p>
                            <p className="text-xs text-text-muted">{item.item_code}</p>
                          </td>
                          <td className="px-4 py-2">{item.quantity_returned}</td>
                          <td className="px-4 py-2">{formatCurrency(item.unit_price)}</td>
                          <td className="px-4 py-2">{formatCurrency(item.line_total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {selectedReturnData.notes && (
                <div>
                  <p className="text-sm text-text-muted">Notes</p>
                  <p className="text-sm">{selectedReturnData.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function getStatusColorClass(variant: string) {
  switch (variant) {
    case 'success':
      return 'bg-green-100 text-green-600';
    case 'warning':
      return 'bg-yellow-100 text-yellow-600';
    case 'error':
      return 'bg-red-100 text-red-600';
    case 'info':
      return 'bg-blue-100 text-blue-600';
    default:
      return 'bg-gray-100 text-gray-600';
  }
}
